import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Phone, Linkedin, Search, X } from 'lucide-react';
import api from '../lib/api';
import { sanitizeHtml } from '../lib/sanitize';
import { useContent } from '../context/ContentContext';

export default function OurPeople() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState('');
  const { content } = useContent();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/team'); setMembers(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const hay = `${m.name} ${m.role} ${(m.specialisms || []).join(' ')} ${m.qualifications || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [members, query]);

  const intro = content?.our_team_intro || 'Meet the friendly, approachable team behind our "Expertise with Care" ethos: solicitors and support staff who put our clients first.';

  return (
    <div className="pt-24">
      <section className="bg-forest-950 py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-5 reveal">The Perrin Myddelton family</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-7xl leading-tight reveal">Our Team</h1>
          <p className="mt-6 text-cream-100/75 text-lg max-w-3xl reveal leading-relaxed">{intro}</p>
        </div>
      </section>

      <section className="bg-forest-900 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? <p className="text-cream-100/60">Loading team...</p> : (
            members.length === 0 ? <p className="text-cream-100/60">Our team profiles are being updated. Please check back shortly.</p> : (
              <>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-10">
                  <div className="relative w-full md:w-80">
                    <Search className="h-4 w-4 text-cream-100/50 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by name or role..."
                      className="w-full bg-forest-950 border border-forest-800 focus:border-sage-500 outline-none pl-10 pr-9 py-2.5 text-sm text-cream-50 placeholder:text-cream-100/40"
                      aria-label="Search team members"
                    />
                    {query && (
                      <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-cream-100/50 hover:text-sage-400" aria-label="Clear search">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs uppercase tracking-[0.25em] text-cream-100/50">Showing {filtered.length} of {members.length}</p>
                </div>

                {filtered.length === 0 ? (
                  <p className="text-cream-100/60 py-12 text-center">No team members match "{query}". Try a different name or role.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                    {filtered.map((m, i) => (
                      <button key={m.id} onClick={() => setActive(m)} className="group text-left bg-forest-950 border border-forest-800 hover:border-sage-500/50 transition-colors animate-fade-up" style={{ animationDelay: `${Math.min(i, 12) * 50}ms` }}>
                        <div className="aspect-[4/5] overflow-hidden bg-forest-800">
                          {m.photo ? (
                            <img src={m.photo} alt={m.name} loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center relative overflow-hidden" style={{background: 'linear-gradient(135deg, #12332b 0%, #1a4437 100%)'}}>
                              <span className="font-serif text-cream-50/85 text-5xl md:text-6xl tracking-wider select-none group-hover:text-sage-400 transition-colors">
                                {m.name.split(' ').filter(Boolean).map(n=>n[0]).slice(0,2).join('')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-serif text-cream-50 text-lg leading-tight group-hover:text-sage-400 transition-colors">{m.name}</h3>
                          <p className="text-sage-400 text-[10px] uppercase tracking-[0.2em] mt-1.5 leading-snug">{m.role}</p>
                          {m.qualifications && <p className="mt-1 text-cream-100/55 text-[11px] italic leading-snug">{m.qualifications}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )
          )}
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fade-up" onClick={() => setActive(null)}>
          <div className="bg-forest-900 border border-forest-800 max-w-5xl w-full max-h-[92vh] overflow-y-auto overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(280px,340px)_1fr] gap-0">
              <div className="bg-forest-950 aspect-square md:aspect-auto md:h-full md:min-h-[520px]">
                {active.photo ? (
                  <img src={active.photo} alt={active.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #12332b 0%, #1a4437 100%)'}}>
                    <span className="font-serif text-cream-50/90 text-7xl md:text-8xl tracking-wider select-none">
                      {active.name.split(' ').filter(Boolean).map(n=>n[0]).slice(0,2).join('')}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-8 md:p-10 min-w-0">
                <button onClick={() => setActive(null)} className="text-cream-100/60 hover:text-cream-50 text-sm mb-4">Close ✕</button>
                <h2 className="font-serif text-cream-50 text-3xl md:text-4xl leading-tight">{active.name}</h2>
                <p className="text-sage-400 text-[11px] uppercase tracking-[0.25em] mt-2">{active.role}</p>
                {active.qualifications && <p className="mt-1 text-cream-100/60 italic">{active.qualifications}</p>}
                {active.years_qualified && <p className="mt-1 text-cream-100/60 text-sm">Qualified {active.years_qualified} year{active.years_qualified > 1 ? 's' : ''}</p>}

                {active.specialisms && active.specialisms.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-widest text-cream-100/50 mb-2">Specialisms</p>
                    <div className="flex flex-wrap gap-2">
                      {active.specialisms.map((s, i) => (
                        <span key={`${s}-${i}`} className="text-xs px-3 py-1 bg-sage-500/10 text-sage-400 border border-sage-500/25">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {active.bio ? (
                  <div className="mt-6 pm-prose text-cream-100/80 text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(active.bio) }} />
                ) : (
                  <p className="mt-6 text-cream-100/60 text-sm italic">A full biography is coming soon. In the meantime, please use the contact details below to get in touch directly.</p>
                )}

                <div className="mt-6 pt-5 border-t border-forest-800 space-y-2 text-sm">
                  {active.email && <a href={`mailto:${active.email}`} className="flex items-center gap-2 text-cream-100/80 hover:text-sage-400"><Mail className="h-4 w-4 text-sage-400" />{active.email}</a>}
                  {active.phone && <a href={`tel:${active.phone.replace(/\s/g,'')}`} className="flex items-center gap-2 text-cream-100/80 hover:text-sage-400"><Phone className="h-4 w-4 text-sage-400" />{active.phone}</a>}
                  {active.linkedin_url && <a href={active.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cream-100/80 hover:text-sage-400"><Linkedin className="h-4 w-4 text-sage-400" />LinkedIn profile</a>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
